import { useEffect, useMemo, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Box, Inbox, Map as MapIcon, Pencil, SlidersHorizontal, Video, ZoomIn, ZoomOut } from 'lucide-react';
import type { AisleDefinition, FloorplanZoneDisplayDefaults, FloorplanZoneOutlineShape, StoreProfileDetail } from '../types';
import {
  getCampaignsForAisle,
  getWalkthroughsForAisle,
} from '../lib/aisleMediaLinks';
import { cacheBustUrl } from '../lib/cacheBustUrl';
import { FloorplanCanvas } from './FloorplanCanvas';
import { UnmappedEvidenceInbox } from './UnmappedEvidenceInbox';
import { TwinReadinessPanel } from './TwinReadinessPanel';
import { ProvenanceBadge } from './ProvenanceBadge';
import { TwinLocationCampaignsPanel } from './TwinLocationCampaignsPanel';
import { buildZoneOutlineColors } from '../lib/instoreLocationGroups';
import { DEFAULT_ZONE_DISPLAY } from '../lib/floorplanZoneOutline';

const ZONE_DISPLAY_STORAGE_KEY = 'store-profile-zone-display';

function loadZoneDisplayDefaults(storeId?: number): FloorplanZoneDisplayDefaults {
  if (storeId == null || typeof window === 'undefined') return DEFAULT_ZONE_DISPLAY;
  try {
    const raw = window.localStorage.getItem(`${ZONE_DISPLAY_STORAGE_KEY}:${storeId}`);
    if (!raw) return DEFAULT_ZONE_DISPLAY;
    return { ...DEFAULT_ZONE_DISPLAY, ...JSON.parse(raw) as FloorplanZoneDisplayDefaults };
  } catch {
    return DEFAULT_ZONE_DISPLAY;
  }
}

function saveZoneDisplayDefaults(storeId: number | undefined, defaults: FloorplanZoneDisplayDefaults) {
  if (storeId == null || typeof window === 'undefined') return;
  window.localStorage.setItem(`${ZONE_DISPLAY_STORAGE_KEY}:${storeId}`, JSON.stringify(defaults));
}

interface TwinWorkspaceProps {
  profile: StoreProfileDetail;
  storeId?: number;
  storeIdParam?: string;
  focusedAisleNumber?: number | null;
  onFocusAisle?: (aisleNumber: number | null) => void;
  onOpenFloorplanSetup?: () => void;
  onOpenWalkthroughs?: () => void;
  onOpenImagery?: () => void;
  manageMode?: boolean;
}

type Selection = { kind: 'aisle' | 'zone'; id: string } | null;

type LayerKey = 'layout' | 'media' | 'campaigns' | 'issues' | 'evidence';

const LAYER_LABELS: Record<LayerKey, string> = {
  layout: 'Store layout',
  media: 'Media inventory',
  campaigns: 'Campaigns',
  issues: 'Issues',
  evidence: 'Evidence',
};

const DEFAULT_LAYERS: Record<LayerKey, boolean> = {
  layout: true,
  media: true,
  campaigns: false,
  issues: false,
  evidence: true,
};

/**
 * The digital twin workspace: central floorplan canvas, aisle/zone navigator,
 * contextual detail panel, layer controls and the unmapped-evidence inbox.
 * Selection is shared with the parent page so "Open in Digital Twin" from a
 * walkthrough lands on the right aisle.
 */
export function TwinWorkspace({
  profile,
  storeId,
  storeIdParam,
  focusedAisleNumber,
  onFocusAisle,
  onOpenFloorplanSetup,
  onOpenWalkthroughs,
  manageMode = false,
}: TwinWorkspaceProps) {
  const [selection, setSelection] = useState<Selection>(null);
  const [selectedInstoreLocationId, setSelectedInstoreLocationId] = useState<number | null>(null);
  const [layers, setLayers] = useState<Record<LayerKey, boolean>>(DEFAULT_LAYERS);
  const [zoom, setZoom] = useState(1);
  const [inboxOpen, setInboxOpen] = useState(false);
  const [zoneDisplayDefaults, setZoneDisplayDefaults] = useState<FloorplanZoneDisplayDefaults>(() =>
    loadZoneDisplayDefaults(storeId),
  );

  useEffect(() => {
    setZoneDisplayDefaults(loadZoneDisplayDefaults(storeId));
  }, [storeId]);

  const updateZoneDisplay = (patch: Partial<FloorplanZoneDisplayDefaults>) => {
    setZoneDisplayDefaults((current) => {
      const next = { ...current, ...patch };
      saveZoneDisplayDefaults(storeId, next);
      return next;
    });
  };

  const floorplanDisplayUrl = cacheBustUrl(profile.floorplanImageUrl, profile.floorplanImageVersion);
  const unmappedTotal = profile.evidenceMappingStats.needsReview + profile.evidenceMappingStats.unplaced;
  const zoneOutlineColors = useMemo(
    () => buildZoneOutlineColors(profile.floorplanZones),
    [profile.floorplanZones],
  );

  useEffect(() => {
    if (focusedAisleNumber == null) return;
    const aisle = profile.aisles.find((item) => item.number === focusedAisleNumber);
    if (aisle) {
      setSelection({ kind: 'aisle', id: aisle.id });
      return;
    }
    // Referenced aisle does not exist canonically — surface the inbox so the
    // user takes the deliberate create-and-attach path instead of landing on an
    // orphaned relationship.
    setInboxOpen(true);
  }, [focusedAisleNumber, profile.aisles]);

  const canvasMedia = useMemo(() => {
    const placed = profile.mediaLocations.filter((media) => media.floorplanAisleId || media.floorplanZoneId);
    let result = layers.media ? placed : [];
    if (layers.campaigns) {
      const campaignMedia = placed.filter((media) => Boolean(media.currentCampaignName));
      result = [...result, ...campaignMedia.filter((m) => !result.includes(m))];
    }
    if (layers.issues) {
      const issueMedia = placed.filter((m) => m.availability === 'unavailable' || m.availability === 'damaged');
      result = [...result, ...issueMedia.filter((m) => !result.includes(m))];
    }
    return result;
  }, [layers.media, layers.campaigns, layers.issues, profile.mediaLocations]);

  const toggleLayer = (key: LayerKey) => {
    setLayers((current) => ({ ...current, [key]: !current[key] }));
  };

  const selectZone = (zoneId: string) => {
    const zone = profile.floorplanZones.find((item) => item.id === zoneId);
    setSelection({ kind: 'zone', id: zoneId });
    setSelectedInstoreLocationId(zone?.instoreLocationId ?? null);
  };

  const selectAisle = (aisle: AisleDefinition) => {
    setSelection({ kind: 'aisle', id: aisle.id });
    onFocusAisle?.(aisle.number);
  };

  const unplacedMediaCount = profile.mediaLocations.length - canvasMedia.length;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2" role="group" aria-label="Floorplan layers">
        {(Object.keys(LAYER_LABELS) as LayerKey[]).map((key) => (
          <label key={key} className="flex items-center gap-1.5 text-sm cursor-pointer">
            <Checkbox
              checked={layers[key]}
              onCheckedChange={() => toggleLayer(key)}
              aria-label={`${LAYER_LABELS[key]} layer`}
            />
            {LAYER_LABELS[key]}
          </label>
        ))}
        <div className="flex flex-wrap items-center gap-2 ml-auto">
          <Popover>
            <PopoverTrigger asChild>
              <Button type="button" variant="outline" size="icon" aria-label="Zone outline settings">
                <SlidersHorizontal className="h-4 w-4" />
              </Button>
            </PopoverTrigger>
            <PopoverContent align="end" className="w-56 space-y-3">
              <p className="text-xs font-medium text-gray-500">Zone outlines</p>
              <div className="space-y-1.5">
                <Label htmlFor="zone-outline-shape" className="text-xs">Shape</Label>
                <Select
                  value={zoneDisplayDefaults.shape ?? DEFAULT_ZONE_DISPLAY.shape}
                  onValueChange={(value: FloorplanZoneOutlineShape) => updateZoneDisplay({ shape: value })}
                >
                  <SelectTrigger id="zone-outline-shape" className="h-8 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="rectangle">Box</SelectItem>
                    <SelectItem value="rounded">Rounded</SelectItem>
                    <SelectItem value="ellipse">Oval</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="zone-outline-width" className="text-xs">Line thickness</Label>
                <input
                  id="zone-outline-width"
                  type="range"
                  min={0.08}
                  max={0.6}
                  step={0.02}
                  value={zoneDisplayDefaults.strokeWidth ?? DEFAULT_ZONE_DISPLAY.strokeWidth}
                  onChange={(event) => updateZoneDisplay({ strokeWidth: Number(event.target.value) })}
                  className="w-full accent-blue-600"
                  aria-label="Zone outline thickness"
                />
              </div>
            </PopoverContent>
          </Popover>
          <Button type="button" variant="outline" size="icon" aria-label="Zoom in" onClick={() => setZoom((z) => Math.min(z + 0.2, 2))}>
            <ZoomIn className="h-4 w-4" />
          </Button>
          <Button type="button" variant="outline" size="icon" aria-label="Zoom out" onClick={() => setZoom((z) => Math.max(z - 0.2, 0.6))}>
            <ZoomOut className="h-4 w-4" />
          </Button>
          {onOpenFloorplanSetup && (
            <Button type="button" variant="outline" size="sm" onClick={onOpenFloorplanSetup}>
              <Pencil className="h-4 w-4 mr-1" aria-hidden />
              Floorplan setup
            </Button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-[240px_minmax(0,1fr)_340px] gap-4">
        <div className="xl:space-y-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm flex items-center justify-between gap-2">
                Store areas
                {!manageMode ? null : (
                  <ProvenanceBadge provenance={profile.aisles.length > 0 || profile.floorplanZones.length > 0 ? 'live' : 'missing'} />
                )}
              </CardTitle>
            </CardHeader>
            <CardContent className="p-2">
              {profile.aisles.length === 0 && profile.floorplanZones.length === 0 && (
                <p className="text-xs text-gray-500 px-2 py-3">
                  No aisles or zones defined yet. Upload a floorplan and define them in setup.
                </p>
              )}
              <div className="flex xl:block gap-1 overflow-x-auto max-h-[420px] xl:overflow-y-auto">
                {profile.aisles.map((aisle) => {
                  const walkCount = getWalkthroughsForAisle(profile.walkthroughs, aisle).length;
                  const campaignCount = getCampaignsForAisle(profile.campaigns, aisle).filter((c) => c.phase === 'current').length;
                  const isSelected = selection?.kind === 'aisle' && selection.id === aisle.id;
                  return (
                    <button
                      key={aisle.id}
                      type="button"
                      onClick={() => selectAisle(aisle)}
                      className={`min-w-[140px] xl:min-w-0 w-full text-left rounded-lg border px-3 py-2 mb-1 text-sm transition-colors ${
                        isSelected ? 'border-blue-500 bg-blue-50' : 'hover:bg-gray-50'
                      }`}
                      aria-pressed={isSelected}
                    >
                      <span className="flex items-center justify-between gap-2">
                        <span className="font-medium">Aisle {aisle.number}</span>
                        <span className="flex gap-1 items-center">
                          {walkCount > 0 && <Video className="h-3.5 w-3.5 text-blue-600" aria-label="Has walk-through" />}
                          {campaignCount > 0 && <Badge variant="secondary" className="text-[10px]">{campaignCount}</Badge>}
                        </span>
                      </span>
                      <span className="block text-xs text-gray-500 truncate">{aisle.name}</span>
                    </button>
                  );
                })}
                {profile.floorplanZones.map((zone) => {
                  const isSelected = selection?.kind === 'zone' && selection.id === zone.id;
                  const dotColor = zoneOutlineColors[zone.id] ?? '#64748b';
                  return (
                    <button
                      key={zone.id}
                      type="button"
                      onClick={() => selectZone(zone.id)}
                      className={`min-w-[140px] xl:min-w-0 w-full text-left rounded-lg border px-3 py-2 mb-1 text-sm transition-colors ${
                        isSelected ? 'border-teal-500 bg-teal-50' : 'hover:bg-gray-50'
                      }`}
                      aria-pressed={isSelected}
                    >
                      <span className="flex items-center gap-2 min-w-0">
                        <span
                          className="h-2.5 w-2.5 rounded-full shrink-0 ring-2 ring-white"
                          style={{ backgroundColor: dotColor }}
                          aria-hidden
                        />
                        <span className="font-medium truncate flex-1">{zone.label}</span>
                      </span>
                    </button>
                  );
                })}
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-3">
              <Button
                type="button"
                variant="outline"
                size="sm"
                className="w-full justify-between"
                onClick={() => setInboxOpen(true)}
              >
                <span className="flex items-center gap-2">
                  <Inbox className="h-4 w-4" aria-hidden />
                  {manageMode ? 'Photos awaiting placement' : 'Unmapped evidence'}
                </span>
                <Badge variant={unmappedTotal > 0 ? 'default' : 'secondary'}>
                  {unmappedTotal}
                </Badge>
                {manageMode && unmappedTotal > 0 && (
                  <span className="sr-only">
                    {profile.evidenceMappingStats.needsReview} suggested, {profile.evidenceMappingStats.unplaced} unplaced
                  </span>
                )}
              </Button>
            </CardContent>
          </Card>
        </div>

        <Card className="min-w-0">
          <CardContent className="p-3 overflow-auto">
            {profile.aisles.length === 0 && profile.floorplanZones.length === 0 && !profile.floorplanImageUrl ? (
              <div className="min-h-[420px] flex flex-col items-center justify-center text-center gap-3 text-gray-500">
                <Box className="h-12 w-12 opacity-30" aria-hidden />
                <div>
                  <p className="font-medium">This store has no digital twin yet</p>
                  <p className="text-sm">Upload a floorplan and define aisles to start building it.</p>
                </div>
                {onOpenFloorplanSetup && (
                  <Button type="button" size="sm" onClick={onOpenFloorplanSetup}>
                    <MapIcon className="h-4 w-4 mr-1" aria-hidden />
                    Open floorplan setup
                  </Button>
                )}
              </div>
            ) : (
              <FloorplanCanvas
                zones={profile.floorplanZones}
                aisles={layers.layout ? profile.aisles : []}
                imageUrl={floorplanDisplayUrl}
                selectedAisleId={selection?.kind === 'aisle' ? selection.id : null}
                selectedZoneId={selection?.kind === 'zone' ? selection.id : null}
                onAisleClick={(aisleId) => {
                  const aisle = profile.aisles.find((item) => item.id === aisleId);
                  if (aisle) selectAisle(aisle);
                }}
                onZoneClick={selectZone}
                mediaLocations={canvasMedia}
                showMedia={canvasMedia.length > 0}
                zoom={zoom}
                zoneOutlineColors={zoneOutlineColors}
                zoneDisplayDefaults={zoneDisplayDefaults}
                minimalOverlays
              />
            )}
            {unplacedMediaCount > 0 && canvasMedia.length >= 0 && (
              <p className="text-xs text-gray-500 mt-2">
                {unplacedMediaCount} media location{unplacedMediaCount === 1 ? '' : 's'} not yet placed on the floorplan —
                assign them from Media inventory.
              </p>
            )}
          </CardContent>
        </Card>

        <TwinLocationCampaignsPanel
          profile={profile}
          storeId={storeId}
          storeIdParam={storeIdParam}
          selectedZoneId={selection?.kind === 'zone' ? selection.id : null}
          selectedInstoreLocationId={selectedInstoreLocationId}
          zoneOutlineColors={zoneOutlineColors}
          onSelectZone={(zoneId) => {
            if (zoneId) selectZone(zoneId);
            else setSelection(null);
          }}
          onSelectInstoreLocation={setSelectedInstoreLocationId}
        />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <TwinReadinessPanel
          profile={profile}
          storeId={storeId}
          storeIdParam={storeIdParam}
          onStepAction={(stepId) => {
            if (stepId === 'floorplan' || stepId === 'aisles') onOpenFloorplanSetup?.();
            else if (stepId === 'evidence') onOpenWalkthroughs?.();
            else if (stepId === 'mappings' || stepId === 'unmapped') setInboxOpen(true);
          }}
        />
      </div>

      <Dialog open={inboxOpen} onOpenChange={setInboxOpen}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{manageMode ? 'Photos awaiting placement' : 'Unmapped evidence'}</DialogTitle>
            <DialogDescription>
              {manageMode
                ? `${profile.evidenceMappingStats.needsReview} suggested locations and ${profile.evidenceMappingStats.unplaced} items still need to be placed on the floorplan.`
                : 'Photos and videos that still need a store area. Assigning never duplicates locations — new areas are only created deliberately.'}
            </DialogDescription>
          </DialogHeader>
          <UnmappedEvidenceInbox
            profile={profile}
            storeId={storeId}
            storeIdParam={storeIdParam}
            onAssigned={(aisleId, zoneId) => {
              if (aisleId) setSelection({ kind: 'aisle', id: aisleId });
              else if (zoneId) setSelection({ kind: 'zone', id: zoneId });
            }}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
}
